import React from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { imgUrl, money } from "../../lib/api";
import SafeImage from "../common/SafeImage";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { useBranding } from "../../context/BrandingContext";
import { getProductCardStyles } from "../../theme/storeStyles";
import { toast } from "sonner";

export default function ProductCard({ product }) {
  const { toggleWishlist, inWishlist } = useCart();
  const { user } = useAuth();
  const { branding } = useBranding();
  const styles = getProductCardStyles(branding?.theme_preset);
  const liked = inWishlist?.(product.id);
  const off = product.mrp > product.price
    ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
    : 0;

  const onLike = async (e) => {
    e.preventDefault();
    if (!user) { toast.info("Sign in to save to wishlist"); return; }
    try {
      const added = await toggleWishlist(product.id);
      toast.success(added ? "Added to wishlist" : "Removed from wishlist");
    } catch {
      toast.error("Could not update wishlist");
    }
  };

  return (
    <Link
      to={`/product/${product.slug}`}
      data-testid={`product-card-${product.slug}`}
      className={styles.cardWrap}
    >
      <div className={styles.imageWrap}>
        <SafeImage
          src={imgUrl(product.images?.[0])}
          alt={product.title}
          className="w-full h-full object-cover kb-product-img"
          loading="lazy"
        />
        {off > 0 && (
          <div className={`absolute top-3 left-3 ${styles.discountBadge}`}>
            −{off}%
          </div>
        )}
        <button
          onClick={onLike}
          type="button"
          className="absolute top-3 right-3 bg-white/90 p-2 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label={liked ? "Remove from wishlist" : "Add to wishlist"}
          data-testid={`wishlist-btn-${product.slug}`}
        >
          <Heart size={16} className={liked ? "fill-[hsl(var(--kb-primary))] text-[hsl(var(--kb-primary))]" : ""} />
        </button>
      </div>
      <div className={styles.info}>
        {product.seller?.shop_name && (
          <div className={styles.seller}>
            {product.seller.shop_name}
          </div>
        )}
        <h3 className={styles.title}>{product.title}</h3>
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className={styles.price}>{money(product.price)}</span>
          {product.mrp > product.price && (
            <span className="text-sm text-[hsl(var(--kb-muted))] line-through">{money(product.mrp)}</span>
          )}
        </div>
      </div>
    </Link>
  );
}
